import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Alert,
    Badge,
    Box,
    Button,
    Flex,
    Heading,
    HStack,
    SimpleGrid,
    Skeleton,
    Spinner,
    Text,
    VStack,
} from '@chakra-ui/react';
import {
    FiActivity,
    FiArrowRight,
    FiBarChart2,
    FiBookOpen,
    FiBriefcase,
    FiChevronRight,
    FiClock,
    FiFileText,
    FiGrid,
    FiLock,
    FiRefreshCw,
    FiTrendingUp,
} from 'react-icons/fi';

import { ExternalLink } from '../../components/ExternalLink';
import { useColorModeValue } from '../../components/ui/color-mode';
import openinsiderLogo from '../../assets/openinsider_favicon.png';
import yahooFinanceLogo from '../../assets/yahoo_finance_logo.png';
import seekingAlphaLogo from '../../assets/seeking_alpha_favicon.png';
import secEdgarLogoPNG from '../../assets/SEC_EDGAR_favicon.png';
import zacksLogoPNG from '../../assets/zacks_logo.png';
import StockNotes from './StockNotes';
import StockPriceHistoryChart from './StockPriceHistoryChart';

function ClassificationLink({ icon: Icon, label, to, value, colorPalette }) {
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const hoverBg = useColorModeValue('gray.50', 'gray.800');

    return (
        <Box
            as={RouterLink}
            to={to}
            aria-label={`Browse companies in ${label.toLowerCase()} ${value}`}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            minW={{ base: '100%', sm: 'auto' }}
            px={3}
            py={2}
            _hover={{ bg: hoverBg, borderColor: `${colorPalette}.400`, textDecoration: 'none' }}
            _focusVisible={{ outline: '2px solid', outlineColor: `${colorPalette}.500`, outlineOffset: '2px' }}
            transition="background 0.15s ease, border-color 0.15s ease"
        >
            <Flex align="center" gap={3}>
                <Flex
                    align="center"
                    justify="center"
                    flex="0 0 auto"
                    boxSize="30px"
                    borderRadius="md"
                    bg={`${colorPalette}.50`}
                    color={`${colorPalette}.700`}
                    _dark={{ bg: `${colorPalette}.900`, color: `${colorPalette}.200` }}
                >
                    <Icon aria-hidden="true" />
                </Flex>
                <Box minW={0} flex="1">
                    <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        letterSpacing="wide"
                        textTransform="uppercase"
                        color="gray.500"
                    >
                        {label}
                    </Text>
                    <Text fontWeight="semibold" color={`${colorPalette}.700`} _dark={{ color: `${colorPalette}.200` }}>
                        {value}
                    </Text>
                </Box>
                <FiChevronRight aria-hidden="true" />
            </Flex>
        </Box>
    );
}

const formatDate = (value) => {
    if (!value) return 'No date';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value).split('T')[0] || 'No date';
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatPercent = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return null;
    return `${numeric.toFixed(1).replace(/\.0$/, '')}%`;
};

const formatLargeNumber = (value, prefix = '$') => {
    if (value === null || value === undefined || value === '') return null;
    const numeric = Number(String(value ?? '').replace(/,/g, ''));
    if (!Number.isFinite(numeric)) return null;

    const abs = Math.abs(numeric);
    const sign = numeric < 0 ? '-' : '';
    if (abs >= 1e12) return `${sign}${prefix}${(abs / 1e12).toFixed(2)}T`;
    if (abs >= 1e9) return `${sign}${prefix}${(abs / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `${sign}${prefix}${(abs / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `${sign}${prefix}${(abs / 1e3).toFixed(1)}K`;
    return `${sign}${prefix}${abs.toFixed(0)}`;
};

const getSavedScenarioLabel = (saved) => {
    if (saved?.name) return saved.name;
    const rawDate = saved?.updated_at || saved?.created_at;
    return `Unnamed scenario - ${formatDate(rawDate)}`;
};

function SectionPanel({ title, actionLabel, to, children }) {
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const bg = useColorModeValue('white', 'gray.900');

    return (
        <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" bg={bg} p={{ base: 4, md: 5 }}>
            <Flex justify="space-between" align="center" gap={3} mb={4}>
                <Heading size="md">{title}</Heading>
                {to && actionLabel && (
                    <HStack
                        as={RouterLink}
                        to={to}
                        gap={1}
                        color="blue.600"
                        fontSize="sm"
                        fontWeight="semibold"
                        flexShrink={0}
                    >
                        <Text>{actionLabel}</Text>
                        <FiArrowRight />
                    </HStack>
                )}
            </Flex>
            {children}
        </Box>
    );
}

function DashboardTile({ icon: Icon, title, preview, to, colorPalette = 'blue' }) {
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const bg = useColorModeValue('white', 'gray.900');
    const hoverBg = useColorModeValue('gray.50', 'gray.800');
    const mutedText = useColorModeValue('gray.600', 'gray.400');

    return (
        <Box
            as={RouterLink}
            to={to}
            display="flex"
            flexDirection="column"
            minH="138px"
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            bg={bg}
            p={3.5}
            transition="border-color 120ms ease, background 120ms ease, transform 120ms ease"
            _hover={{ borderColor: `${colorPalette}.400`, bg: hoverBg, transform: 'translateY(-1px)' }}
        >
            <Flex justify="space-between" align="flex-start" gap={3}>
                <Box
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    boxSize="34px"
                    borderRadius="md"
                    bg={`${colorPalette}.100`}
                    color={`${colorPalette}.700`}
                    flexShrink={0}
                >
                    <Icon />
                </Box>
                <FiArrowRight />
            </Flex>
            <Heading
                as="h2"
                mt={3}
                mb={1.5}
                fontSize={{ base: 'xl', md: '2xl' }}
                lineHeight="1.12"
                letterSpacing="0"
            >
                {title}
            </Heading>
            <Text fontSize="sm" color={mutedText} lineHeight="1.45">
                {preview}
            </Text>
        </Box>
    );
}

const formatPrice = (value) => {
    if (value === null || value === undefined || String(value).trim() === '') return '—';
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '—';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(numeric);
};

const getPlottedPrice = (point) => {
    const adjusted = point?.adj_close === null || point?.adj_close === undefined
        ? NaN
        : Number(point.adj_close);
    if (Number.isFinite(adjusted)) return adjusted;
    const close = point?.close === null || point?.close === undefined ? NaN : Number(point.close);
    return Number.isFinite(close) ? close : null;
};

function MarketMetric({ label, value, detail, tone = 'default' }) {
    const mutedText = useColorModeValue('gray.600', 'gray.400');
    const valueColor = tone === 'positive'
        ? 'green.600'
        : tone === 'negative'
            ? 'red.600'
            : undefined;

    return (
        <Box minW={0}>
            <Text fontSize="xs" color={mutedText} textTransform="uppercase" letterSpacing="wide">
                {label}
            </Text>
            <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="bold" color={valueColor} whiteSpace="nowrap">
                {value}
            </Text>
            {detail ? <Text fontSize="xs" color={mutedText}>{detail}</Text> : null}
        </Box>
    );
}

function PriceOverview({ history, range, rangeOptions, setRange, quote, routes }) {
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const panelBg = useColorModeValue('white', 'gray.900');
    const mutedText = useColorModeValue('gray.600', 'gray.400');
    const metrics = useMemo(() => {
        const plotted = (history.prices || [])
            .map((point) => ({ point, value: getPlottedPrice(point) }))
            .filter(({ value }) => value !== null);
        const first = plotted[0]?.value ?? null;
        const last = Number(quote?.data?.close) || plotted[plotted.length - 1]?.value || null;
        const previous = plotted.length > 1 ? plotted[plotted.length - 2]?.value : first;
        const dailyChange = last !== null && previous !== null ? last - previous : null;
        const dailyPercent = dailyChange !== null && previous ? (dailyChange / previous) * 100 : null;
        const values = plotted.map(({ value }) => value);
        return {
            current: last,
            dailyChange,
            dailyPercent,
            low: values.length ? Math.min(...values) : null,
            high: values.length ? Math.max(...values) : null,
            marketCap: quote?.data?.market_cap ?? null,
        };
    }, [history.prices, quote?.data]);
    const changeTone = metrics.dailyChange > 0 ? 'positive' : metrics.dailyChange < 0 ? 'negative' : 'default';
    const changePrefix = metrics.dailyChange > 0 ? '+' : '';

    return (
        <Box borderWidth="1px" borderColor={borderColor} borderRadius="xl" bg={panelBg} overflow="hidden" mb={5}>
            <Flex
                align={{ base: 'flex-start', lg: 'center' }}
                justify="space-between"
                direction={{ base: 'column', lg: 'row' }}
                gap={4}
                px={{ base: 4, md: 5 }}
                pt={4}
            >
                <Box>
                    <Heading size="md">Stock price</Heading>
                    <Text fontSize="sm" color={mutedText}>Adjusted close with daily trading volume</Text>
                </Box>
                <SimpleGrid columns={{ base: 2, md: 4 }} gap={{ base: 4, md: 8 }} w={{ base: 'full', lg: 'auto' }}>
                    <MarketMetric label="Last close" value={formatPrice(metrics.current)} />
                    <MarketMetric
                        label="Daily change"
                        value={metrics.dailyChange === null
                            ? '—'
                            : `${changePrefix}${formatPrice(metrics.dailyChange)} (${changePrefix}${metrics.dailyPercent.toFixed(2)}%)`}
                        tone={changeTone}
                    />
                    <MarketMetric label="Market cap" value={formatLargeNumber(metrics.marketCap) || '—'} />
                    <MarketMetric
                        label={`${rangeOptions[range]?.label || '1Y'} range`}
                        value={metrics.low === null ? '—' : `${formatPrice(metrics.low)} – ${formatPrice(metrics.high)}`}
                    />
                </SimpleGrid>
            </Flex>
            <Flex px={{ base: 4, md: 5 }} pt={4} align="center" justify="space-between" gap={3} wrap="wrap">
                <HStack gap={1}>
                    {Object.entries(rangeOptions).map(([key, option]) => (
                        <Button
                            key={key}
                            size="xs"
                            variant={range === key ? 'solid' : 'ghost'}
                            colorPalette="teal"
                            onClick={() => setRange(key)}
                            aria-pressed={range === key}
                        >
                            {option.label}
                        </Button>
                    ))}
                </HStack>
                <HStack
                    as={RouterLink}
                    to={routes.charts}
                    gap={1}
                    color="blue.600"
                    fontSize="xs"
                    fontWeight="semibold"
                    px={2}
                    py={1}
                    borderRadius="md"
                    _hover={{ bg: 'blue.50', textDecoration: 'none' }}
                >
                    <Text>Open full chart</Text>
                    <FiArrowRight />
                </HStack>
            </Flex>
            <Box p={{ base: 3, md: 4 }} pt={2}>
                <StockPriceHistoryChart
                    prices={history.prices}
                    loading={history.loading}
                    error={history.error}
                    priceStatus={history.status}
                    priceMessage={history.message}
                />
            </Box>
        </Box>
    );
}

function FilingPreviewList({ filings, loading, error, emptyMessage }) {
    const mutedText = useColorModeValue('gray.600', 'gray.400');
    const borderColor = useColorModeValue('gray.200', 'gray.700');

    if (loading) {
        return (
            <VStack align="stretch" gap={3}>
                <Skeleton height="48px" />
                <Skeleton height="48px" />
                <Skeleton height="48px" />
            </VStack>
        );
    }

    if (error) {
        return (
            <Alert.Root status="warning">
                <Alert.Indicator />
                <Alert.Content>{error}</Alert.Content>
            </Alert.Root>
        );
    }

    if (!filings.length) {
        return <Text color={mutedText}>{emptyMessage}</Text>;
    }

    return (
        <VStack align="stretch" gap={3}>
            {filings.map((filing) => {
                const aiTags = filing.ai?.masterTags?.length ? filing.ai.masterTags : filing.ai?.tags || [];
                return (
                    <Box key={filing.id} borderBottom="1px solid" borderColor={borderColor} pb={3}>
                        <Flex gap={2} align="center" wrap="wrap" mb={1}>
                            <Badge colorPalette="blue" variant="subtle">{filing.formType}</Badge>
                            <Text fontSize="sm" color={mutedText}>{formatDate(filing.filingDate)}</Text>
                        </Flex>
                        <Text fontSize="sm" fontWeight="medium" lineClamp={2}>
                            {filing.ai?.summary140 || filing.description || 'SEC filing'}
                        </Text>
                        {aiTags.length > 0 && (
                            <HStack gap={2} mt={2} wrap="wrap">
                                {aiTags.slice(0, 3).map((tag) => (
                                    <Badge key={tag} colorPalette="gray" variant="outline" fontSize="xs">
                                        {String(tag).replace(/_/g, ' ')}
                                    </Badge>
                                ))}
                            </HStack>
                        )}
                    </Box>
                );
            })}
        </VStack>
    );
}

const getScenarioRoute = (saved, routes) => {
    if (saved.kind !== 'dcf') return routes.reverseDcf;
    if (saved.id == null) return routes.analysis;
    return `${routes.analysis}?savedScenario=${encodeURIComponent(saved.id)}`;
};

const getScenarioKindLabel = (saved) => (
    saved.kind === 'dcf' ? 'DCF Analysis' : 'Reverse DCF'
);

function ScenarioPreview({ isAuthenticated, isAuthLoading, saves, loading, error, routes }) {
    const mutedText = useColorModeValue('gray.600', 'gray.400');
    const borderColor = useColorModeValue('gray.200', 'gray.700');

    if (isAuthLoading) {
        return (
            <HStack gap={2} color={mutedText}>
                <Spinner size="sm" />
                <Text fontSize="sm">Checking saved scenarios…</Text>
            </HStack>
        );
    }

    if (!isAuthenticated) {
        return (
            <HStack gap={2} color={mutedText}>
                <FiLock />
                <Text fontSize="sm">Sign in to see saved DCF scenarios.</Text>
            </HStack>
        );
    }

    if (loading) {
        return (
            <VStack align="stretch" gap={3}>
                <Skeleton height="44px" />
                <Skeleton height="44px" />
            </VStack>
        );
    }

    if (error) {
        return (
            <Alert.Root status="warning">
                <Alert.Indicator />
                <Alert.Content>{error}</Alert.Content>
            </Alert.Root>
        );
    }

    if (!saves.length) {
        return (
            <Text color={mutedText}>
                No saved scenarios yet. Open DCF Analysis or Reverse DCF to save assumptions for this ticker.
            </Text>
        );
    }

    return (
        <VStack align="stretch" gap={3}>
            {saves.slice(0, 3).map((saved) => {
                const assumptions = saved.assumptions || {};
                const wacc = formatPercent(assumptions.wacc);
                const terminalGrowth = formatPercent(assumptions.terminalGrowth);
                const fcf = formatLargeNumber(assumptions.fcfTtm);
                const revenueGrowth = formatPercent(assumptions.stage1Growth);
                const operatingMargin = formatPercent(assumptions.operatingMarginTarget);
                const details = [wacc && `WACC ${wacc}`, terminalGrowth && `Terminal ${terminalGrowth}`, fcf && `FCF ${fcf}`]
                    .concat([revenueGrowth && `Growth ${revenueGrowth}`, operatingMargin && `Margin ${operatingMargin}`])
                    .filter(Boolean)
                    .join(' • ');

                return (
                    <Box
                        key={saved.id || `${saved.name}-${saved.updated_at}`}
                        as={RouterLink}
                        to={getScenarioRoute(saved, routes)}
                        borderWidth="1px"
                        borderColor={borderColor}
                        borderRadius="md"
                        p={3}
                        _hover={{ borderColor: 'green.400' }}
                    >
                        <Flex justify="space-between" gap={3} align="flex-start">
                            <Box minW={0}>
                                <HStack gap={2} wrap="wrap">
                                    <Text fontWeight="semibold" truncate>{getSavedScenarioLabel(saved)}</Text>
                                    <Badge colorPalette={saved.kind === 'dcf' ? 'teal' : 'green'} variant="subtle">
                                        {getScenarioKindLabel(saved)}
                                    </Badge>
                                </HStack>
                                <Text fontSize="xs" color={mutedText}>
                                    Updated {formatDate(saved.updated_at || saved.created_at)}
                                </Text>
                                {details && (
                                    <Text fontSize="sm" color={mutedText} mt={1}>{details}</Text>
                                )}
                            </Box>
                            <FiArrowRight />
                        </Flex>
                    </Box>
                );
            })}
        </VStack>
    );
}

function StockHomeDashboard({
    externalLinks,
    filingsError,
    filingsLoading,
    insiderFilings,
    isAuthenticated,
    isAuthLoading,
    latestFilings,
    publicMetricsCount,
    priceHistory,
    priceRange,
    priceRangeOptions,
    setPriceRange,
    stockPrice,
    dcfError,
    dcfLoading,
    dcfSaves = [],
    reverseDcfError,
    reverseDcfLoading,
    reverseDcfSaves = [],
    routes,
    stockInfo,
    ticker,
    upperCaseTicker,
}) {
    const utilityBorderColor = useColorModeValue('gray.200', 'gray.700');
    const dashboardTiles = useMemo(() => {
        const tiles = [
            {
                title: 'DCF Analysis',
                preview: 'Review free cash flow history, projection inputs, intrinsic value, and AI rating tools.',
                to: routes.analysis,
                icon: FiBarChart2,
                colorPalette: 'teal',
            },
            {
                title: 'Reverse DCF',
                preview: isAuthenticated && reverseDcfSaves.length
                    ? `${reverseDcfSaves.length} saved scenario${reverseDcfSaves.length === 1 ? '' : 's'} for ${upperCaseTicker}.`
                    : 'Work backward from the current market price and save assumption sets.',
                to: routes.reverseDcf,
                icon: FiRefreshCw,
                colorPalette: 'green',
            },
            {
                title: 'SEC Filings',
                preview: filingsLoading
                    ? 'Loading latest filings…'
                    : latestFilings.length
                        ? `${latestFilings.length} recent filing${latestFilings.length === 1 ? '' : 's'} ready to review.`
                        : 'Read annual reports, current reports, proxies, and AI filing summaries.',
                to: routes.filings,
                icon: FiFileText,
                colorPalette: 'blue',
            },
            {
                title: 'Charts',
                preview: 'Scan price action, moving averages, and market context for this ticker.',
                to: routes.charts,
                icon: FiTrendingUp,
                colorPalette: 'purple',
            },
            {
                title: 'Snapshot',
                preview: 'Practice valuation from a historical date with hindsight-safe filings and prices.',
                to: routes.snapshot,
                icon: FiClock,
                colorPalette: 'pink',
            },
            {
                title: 'Financial Statements',
                preview: 'Open income statement, balance sheet, and cash flow statement views.',
                to: routes.financials,
                icon: FiBookOpen,
                colorPalette: 'orange',
            },
        ];

        if (publicMetricsCount > 0) {
            tiles.push({
                title: 'Public KPI Data',
                preview: `${publicMetricsCount} approved operating metric${publicMetricsCount === 1 ? '' : 's'} with filing-sourced historical data.`,
                to: routes.publicMetrics,
                icon: FiActivity,
                colorPalette: 'cyan',
            });
        }

        return tiles;
    }, [filingsLoading, isAuthenticated, latestFilings.length, publicMetricsCount, reverseDcfSaves.length, routes, upperCaseTicker]);

    const scenarioSaves = useMemo(
        () => [
            ...dcfSaves.map((save) => ({ ...save, kind: 'dcf' })),
            ...reverseDcfSaves.map((save) => ({ ...save, kind: 'reverse_dcf' })),
        ].toSorted((a, b) => {
            const dateA = Date.parse(a?.updated_at || a?.created_at || '') || 0;
            const dateB = Date.parse(b?.updated_at || b?.created_at || '') || 0;
            return dateB - dateA;
        }),
        [dcfSaves, reverseDcfSaves]
    );
    const scenarioError = dcfError || reverseDcfError;
    const scenarioLoading = dcfLoading || reverseDcfLoading;

    return (
        <Box p={{ base: 1, md: 4 }}>
            <Box mb={5}>
                <Flex align={{ base: 'flex-start', md: 'baseline' }} gap={{ base: 1, md: 4 }} direction={{ base: 'column', md: 'row' }}>
                    <Heading
                        as="h1"
                        fontSize={{ base: '4xl', md: '5xl' }}
                        lineHeight="1"
                        letterSpacing="tight"
                    >
                        {upperCaseTicker}
                    </Heading>
                    <Text fontSize={{ base: 'lg', md: '2xl' }} color="gray.600" fontWeight="medium">
                        {stockInfo.company_name || 'Unknown Company'}
                    </Text>
                </Flex>
                {(stockInfo.sector || stockInfo.industry) && (
                    <HStack mt={3} gap={2} align="stretch" flexWrap="wrap">
                        {stockInfo.sector && (
                            <ClassificationLink
                                icon={FiGrid}
                                label="Sector"
                                value={stockInfo.sector}
                                colorPalette="teal"
                                to={`/browse?sector=${encodeURIComponent(stockInfo.sector)}`}
                            />
                        )}
                        {stockInfo.industry && (
                            <ClassificationLink
                                icon={FiBriefcase}
                                label="Industry"
                                value={stockInfo.industry}
                                colorPalette="blue"
                                to={`/browse?industry=${encodeURIComponent(stockInfo.industry)}`}
                            />
                        )}
                    </HStack>
                )}
            </Box>

            <PriceOverview
                history={priceHistory}
                quote={stockPrice}
                range={priceRange}
                rangeOptions={priceRangeOptions}
                setRange={setPriceRange}
                routes={routes}
            />

            <Box
                borderWidth="1px"
                borderColor={utilityBorderColor}
                borderRadius="lg"
                px={{ base: 4, md: 5 }}
                py={3}
                mb={5}
            >
                <Flex align={{ base: 'flex-start', md: 'center' }} gap={{ base: 3, md: 6 }} direction={{ base: 'column', md: 'row' }}>
                    <Text fontSize="sm" fontWeight="bold" whiteSpace="nowrap">External resources</Text>
                    <Flex gap={{ base: 3, md: 6 }} wrap="wrap" flex="1">
                        {externalLinks.map((link) => (
                            <Box key={link.text} fontSize="xs">
                                <ExternalLink
                                    href={link.href}
                                    logoSrc={link.logo}
                                    altText={`${link.text} Logo`}
                                >
                                    {link.text}
                                </ExternalLink>
                            </Box>
                        ))}
                    </Flex>
                </Flex>
            </Box>

            <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, '2xl': 6 }} gap={3} mb={6}>
                {dashboardTiles.map((tile) => (
                    <DashboardTile key={tile.title} {...tile} />
                ))}
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, xl: 2 }} gap={4} mb={6}>
                <SectionPanel title="Saved DCF Scenarios" actionLabel="Open DCF Analysis" to={routes.analysis}>
                    <ScenarioPreview
                        isAuthenticated={isAuthenticated}
                        isAuthLoading={isAuthLoading}
                        saves={scenarioSaves}
                        loading={scenarioLoading}
                        error={scenarioError}
                        routes={routes}
                    />
                </SectionPanel>

                <SectionPanel title="DCF Analysis Workspace" actionLabel="Open DCF Analysis" to={routes.analysis}>
                    <VStack align="stretch" gap={3}>
                        <Text color="gray.600">
                            Build a regular DCF from cash-flow history, projection assumptions, and intrinsic value outputs.
                        </Text>
                        <HStack gap={2} wrap="wrap">
                            <Badge colorPalette="teal" variant="subtle">Cash flow history</Badge>
                            <Badge colorPalette="teal" variant="subtle">Projection inputs</Badge>
                            <Badge colorPalette="teal" variant="subtle">AI rating</Badge>
                        </HStack>
                    </VStack>
                </SectionPanel>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, xl: 2 }} gap={4} mb={6}>
                <SectionPanel title="Latest SEC Filings" actionLabel="View all filings" to={routes.filings}>
                    <FilingPreviewList
                        filings={latestFilings}
                        loading={filingsLoading}
                        error={filingsError}
                        emptyMessage={`No SEC filings found for ${upperCaseTicker}.`}
                    />
                </SectionPanel>

                <SectionPanel
                    title="Insider Activity"
                    actionLabel="Open filings view"
                    to={`${routes.filings}?filter=insider-transactions`}
                >
                    <Flex gap={2} align="center" mb={3} color="gray.600">
                        <FiActivity />
                        <Text fontSize="sm">Forms 3, 4, 5, and 144 from the SEC filings feed.</Text>
                    </Flex>
                    <FilingPreviewList
                        filings={insiderFilings}
                        loading={filingsLoading}
                        error={filingsError}
                        emptyMessage={`No recent insider-related filings found for ${upperCaseTicker}.`}
                    />
                </SectionPanel>
            </SimpleGrid>

            <Box mt={6}>
                <Heading size="md" mb={4}>Company Notes</Heading>
                <StockNotes ticker={ticker} />
            </Box>
        </Box>
    );
}

export const buildStockHomeExternalLinks = ({ lowerCaseTicker, stockInfo, upperCaseTicker }) => {
    if (!lowerCaseTicker) {
        return [];
    }

    const links = [
        {
            href: `http://openinsider.com/search?q=${lowerCaseTicker}`,
            logo: openinsiderLogo,
            text: 'Open Insider (Insider Trading)',
        },
        {
            href: `https://seekingalpha.com/symbol/${upperCaseTicker}`,
            logo: seekingAlphaLogo,
            text: 'Seeking Alpha Profile & News',
        },
        {
            href: `https://finance.yahoo.com/quote/${upperCaseTicker}`,
            logo: yahooFinanceLogo,
            text: 'Yahoo Finance Quote & Chart',
        },
        {
            href: stockInfo?.cik
                ? `https://www.sec.gov/edgar/browse/?CIK=${stockInfo.cik}&owner=exclude`
                : null,
            logo: secEdgarLogoPNG,
            text: 'SEC EDGAR Filings',
        },
        {
            href: `https://www.zacks.com/stock/research/${upperCaseTicker}/earnings-calendar`,
            logo: zacksLogoPNG,
            text: 'Zacks Earnings Calendar',
        },
    ];

    return links.filter((link) => link.href);
};

export default StockHomeDashboard;
